import { Parser } from "@siteimprove/alfa-parser";
import { Err } from "@siteimprove/alfa-result";
import { Function, Token, } from "../../syntax/index.js";
import { Keyword } from "../textual/keyword.js";
import { Number, Percentage } from "../numeric/index.js";
import { Format } from "./format.js";
const { pair, map, either, option, right, take, delimited } = Parser;
/**
 * @public
 */
export class RGB extends Format {
    static of(red, green, blue, alpha) {
        return new RGB(red.resolve(), green.resolve(), blue.resolve(), alpha.resolve());
    }
    _red;
    _green;
    _blue;
    _alpha;
    constructor(red, green, blue, alpha) {
        super("rgb");
        this._red = red;
        this._green = green;
        this._blue = blue;
        this._alpha = alpha;
    }
    get red() {
        return this._red;
    }
    get green() {
        return this._green;
    }
    get blue() {
        return this._blue;
    }
    get alpha() {
        return this._alpha;
    }
    resolve() {
        return new RGB(...Format.resolve(this._red, this._green, this._blue, this._alpha));
    }
    equals(value) {
        return (value instanceof RGB &&
            value._red.equals(this._red) &&
            value._green.equals(this._green) &&
            value._blue.equals(this._blue) &&
            value._alpha.equals(this._alpha));
    }
    hash(hash) {
        hash
            .writeHashable(this._red)
            .writeHashable(this._green)
            .writeHashable(this._blue)
            .writeHashable(this._alpha);
    }
    toJSON() {
        return {
            ...super.toJSON(),
            red: this._red.toJSON(),
            green: this._green.toJSON(),
            blue: this._blue.toJSON(),
            alpha: this._alpha.toJSON(),
        };
    }
    toString() {
        return `rgb(${this._red} ${this._green} ${this._blue}${this._alpha.value === 1 ? "" : ` / ${this._alpha}`})`;
    }
}
/**
 * @public
 */
(function (RGB) {
    function isRGB(value) {
        return value instanceof RGB;
    }
    RGB.isRGB = isRGB;
    /**
     * @remarks
     * While the three R, G, B components must be either all numbers or all
     * percentage, the alpha component can be either independently.
     *
     * {@link https://drafts.csswg.org/css-color/#typedef-alpha-value}
     */
    const parseAlphaLegacy = either(Number.parse, (Percentage.parse));
    const parseAlphaModern = either(Number.parse, (Percentage.parse), map(Keyword.parse("none"), () => Percentage.of(0)));
    /**
     * Parses either a number/percentage or the keyword "none", reduces "none" to
     * the correct type, or fails if it is not allowed.
     */
    const parseItem = (parser, ifNone) => either(parser, ifNone !== undefined
        ? map(Keyword.parse("none"), () => ifNone)
        : () => Err.of("none is not accepted in legacy rbg syntax"));
    /**
     * Parses 3 items.
     * In legacy syntax, they must be separated by a comma, in modern syntax by
     * whitespace.
     */
    const parseTriplet = (parser, separator, ifNone) => map(pair(parseItem(parser, ifNone), take(right(separator, parseItem(parser, ifNone)), 2)), ([r, [g, b]]) => [r, g, b]);
    const parseLegacyTriplet = (parser) => parseTriplet(parser, delimited(option(Token.parseWhitespace), Token.parseComma));
    const parseLegacy = pair(either(parseLegacyTriplet((Percentage.parse)), parseLegacyTriplet(Number.parse)), option(right(delimited(option(Token.parseWhitespace), Token.parseComma), parseAlphaLegacy)));
    const parseModernTriplet = (parser, ifNone) => parseTriplet(parser, option(Token.parseWhitespace), ifNone);
    const parseModern = pair(either(parseModernTriplet(Percentage.parse, Percentage.of(0)), parseModernTriplet(Number.parse, Number.of(0))), option(right(delimited(option(Token.parseWhitespace), Token.parseDelim("/")), parseAlphaModern)));
    /**
     * {@link https://drafts.csswg.org/css-color/#funcdef-rgb}
     */
    RGB.parse = map(Function.parse((fn) => fn.value === "rgb" || fn.value === "rgba", either(parseLegacy, parseModern)), (result) => {
        const [, [[red, green, blue], alpha]] = result;
        return RGB.of(red, green, blue, alpha.getOrElse(() => Number.of(1)));
    });
})(RGB || (RGB = {}));
//# sourceMappingURL=rgb.js.map